import React, { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import { ExpandableRow } from 'transfix-ui/components/ExpandableRow'
import { Box, createStyles, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { selectCarrierDetails } from 'utils/reducers/carrier'
import {
  RateType,
  ShipperCityCarrierLane as TShipperCityCarrierLane,
  useGetShipperCityCarrierLanes,
} from 'apps/carrier/apiTypes/shipperCityCarrierLanes'
import useFilters from 'apps/marketplace/utils/hooks/useFilters'
import { Pagination, Tooltip } from 'transfix-ui'
import Loader from './Loader'
import SearchLaneId from './SearchLaneId'
import ShipperCityCarrierLane from './ShipperCityCarrierLane'

const useFuelSurchargeStyles = makeStyles(() =>
  createStyles({
    emptyState: {
      opacity: 0,
      animation: 'fadeIn 500ms ease-in forwards 1s',
    },
    overflow: {
      position: 'absolute',
      bottom: '50px',
      width: '100%',
      height: '120px',
      background: 'linear-gradient(to bottom, rgba(240, 244, 245, 0), #f0f3f5)',
    },
  })
)

export const toggleRate = (rateType: RateType) =>
  rateType === 'flat_rate' ? 'fuel_surcharge' : 'flat_rate'

/**
 * Fuel Surcharge Settings
 */
const FuelSurcharge: FC = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [lanes, setLanes] = useState<TShipperCityCarrierLane[]>([])

  const carrier = useSelector(selectCarrierDetails)
  const carrierApplicationId = Number(carrier?.id)
  const { data, loading, refetch } = useGetShipperCityCarrierLanes({
    lazy: true,
  })

  const allFilters = useFilters({
    currentPage: 1,
    showPagination: false,
    pageTotalCount: 0,
    pageCount: 0,
    perPage: 25,
    filters: {
      carrierApplicationId,
      rateType: 'fuel_surcharge',
    },
  })

  const {
    currentPage = 1,
    filters,
    onPageChange,
    pageTotalCount = 0,
    params: queryParams,
    setFilters,
    setPageTotalCount,
  } = allFilters

  // When data is loaded, set the shipper city lanes and page total
  useEffect(() => {
    if (data?.shipperCityCarrierLanes) {
      setLanes(data.shipperCityCarrierLanes)
      setPageTotalCount(data.totalCount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // Only fetch when Fuel Surcharge section is toggled open
  useEffect(() => {
    if (isExpanded) {
      refetch({ queryParams })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.filters, queryParams.pageCount, isExpanded])

  const setLargerHeight = lanes.length > 10 ? { height: '500px' } : {}
  const classes = useFuelSurchargeStyles()

  const onSearchLaneIdSubmitHandler = (shipperCityLaneId?: number) => {
    setLanes([])
    setFilters({ shipperCityLaneId })
  }

  return (
    <ExpandableRow
      row={
        <Box alignItems='center' display='flex'>
          <Typography variant='h4'>Fuel Surcharge Settings</Typography>
        </Box>
      }
      rowClass='card'
      onExpand={() => setIsExpanded(current => !current)}
      details={
        <Box position='relative'>
          <Box mt={2} mb={3} display='flex' alignItems='center'>
            <Box mr={2}>
              <SearchLaneId onSubmit={onSearchLaneIdSubmitHandler} />
            </Box>
            <Box>
              <Checkbox
                disabled={loading}
                checked={filters.rateType === 'fuel_surcharge'}
                onChange={() => setFilters({ rateType: toggleRate(filters.rateType) })}
                label='Has Fuel Surcharge?'
              />
            </Box>
          </Box>
          <Grid
            container
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            wrap='nowrap'>
            <Grid item xs={1}>
              <Tooltip
                position='bottom'
                trigger={
                  <Box display='flex' justifyContent='center' alignItems='center'>
                    <Box ml={3} mr={1} className='b text-caption text-secondary'>
                      ID
                    </Box>
                    <InfoIcon color='info' className='icon-input' />
                  </Box>
                }>
                <strong>Shipper City Lane ID</strong>
              </Tooltip>
            </Grid>
            <Grid item xs={2}>
              <strong className='text-caption text-secondary'>Shipper</strong>
            </Grid>
            <Grid item xs={1}>
              <Box textAlign='center' className='b text-caption text-secondary'>
                Drop Trailer?
              </Box>
            </Grid>
            <Grid item xs={2}>
              <strong className='text-caption text-secondary'>Pickup City</strong>
            </Grid>
            <Grid item xs={2}>
              <strong className='text-caption text-secondary'>Delivery City</strong>
            </Grid>
            <Grid item xs={2}>
              <Tooltip
                position='bottom'
                trigger={
                  <Box display='flex' justifyContent='center' alignItems='center'>
                    <Box mr={1} className='b text-caption text-secondary'>
                      Fuel Surcharge
                    </Box>
                    <InfoIcon color='info' className='icon-input' />
                  </Box>
                }>
                <strong>Toggle on Fuel Surcharge</strong>
              </Tooltip>
            </Grid>
          </Grid>
          <hr className='bg-disabled mt2' />
          <Box minHeight='300px' {...setLargerHeight} overflow='scroll' paddingBottom='100px'>
            {loading ? (
              <Loader />
            ) : (
              lanes?.map(lane => (
                <ShipperCityCarrierLane lane={lane} key={`${lane.id}-${filters.rateType}`} />
              ))
            )}
            {/* Loading State */}
            {!loading && lanes.length === 0 && isExpanded && (
              <Box padding={3} textAlign='center' className={classes.emptyState}>
                <Typography variant='caption'>NO LANES FOUND</Typography>
              </Box>
            )}
          </Box>
          <Box className={classes.overflow} />
          <Box paddingY={2} display='flex' justifyContent='center'>
            <Pagination
              currentPage={currentPage}
              numberOfPages={pageTotalCount}
              onPageChange={onPageChange}
            />
          </Box>
        </Box>
      }
    />
  )
}

export default FuelSurcharge
